import * as React from 'react';
import { reportBFError } from 'utils/Tracker';
import { ItemValue } from '../components/SelectRich';
import Layer from '../data/Layer';
import {
  PropertyTimeline,
  TimelinePoint,
  TIMELINE_VISIBILITY_KEY
} from '../data/LayerTimeline';
import Point from '../data/Point';
import { getSignalAt, getStoreState } from '../state/Store';
import { formatTimestamp, getCSSVarValue } from '../utils';
import { TIMELINE_RESET } from './EffectTimelineView';

const SNAP_DISTANCE = 5;
const TOP_PADDING = 4;

const clamp = (v: number, min: number = 0, max: number = 1): number => {
  return Math.max(min, Math.min(max, v));
};

/*

from: https://stackoverflow.com/questions/32281168/find-a-point-on-a-line-closest-to-a-third-point-javascript

desc Static function. Find point on lines nearest test point
   test point pXy with properties .x and .y
   lines defined by array aXys with nodes having properties .x and .y 
   return is object with .x and .y properties and property i indicating nearest segment in aXys 
   and property fFrom the fractional distance of the returned point from aXy[i-1]
   and property fTo the fractional distance of the returned point from aXy[i]   */

const getClosestPointOnLines = (
  pXy: TimelinePoint,
  aXys: TimelinePoint[]
): TimelinePoint => {
  var minDist;
  var fTo = 0;
  // var fFrom = 0;
  var position = 0;
  var value = 0;
  var i = 0;
  var dist;

  if (aXys.length > 1) {
    for (var n = 1; n < aXys.length; n++) {
      if (aXys[n].position !== aXys[n - 1].position) {
        var a =
          (aXys[n].value - aXys[n - 1].value) /
          (aXys[n].position - aXys[n - 1].position);
        var b = aXys[n].value - a * aXys[n].position;
        dist =
          Math.abs(a * pXy.position + b - pXy.value) / Math.sqrt(a * a + 1);
      } else {
        dist = Math.abs(pXy.position - aXys[n].position);
      }

      // length^2 of line segment
      var rl2 =
        Math.pow(aXys[n].value - aXys[n - 1].value, 2) +
        Math.pow(aXys[n].position - aXys[n - 1].position, 2);

      // distance^2 of pt to end line segment
      var ln2 =
        Math.pow(aXys[n].value - pXy.value, 2) +
        Math.pow(aXys[n].position - pXy.position, 2);

      // distance^2 of pt to begin line segment
      var lnm12 =
        Math.pow(aXys[n - 1].value - pXy.value, 2) +
        Math.pow(aXys[n - 1].position - pXy.position, 2);

      // minimum distance^2 of pt to infinite line
      var dist2 = Math.pow(dist, 2);

      // calculated length^2 of line segment
      var calcrl2 = ln2 - dist2 + lnm12 - dist2;

      // redefine minimum distance to line segment (not infinite line) if necessary
      if (calcrl2 > rl2) {
        dist = Math.sqrt(Math.min(ln2, lnm12));
      }

      if (minDist == null || minDist > dist) {
        if (calcrl2 > rl2) {
          if (lnm12 < ln2) {
            fTo = 0; // nearer to previous point
            // fFrom = 1;
          } else {
            // fFrom = 0; //nearer to current point
            fTo = 1;
          }
        } else {
          // perpendicular from point intersects line segment
          fTo = Math.sqrt(lnm12 - dist2) / Math.sqrt(rl2);
          // fFrom = Math.sqrt(ln2 - dist2) / Math.sqrt(rl2);
        }
        minDist = dist;
        i = n;
      }
    }

    var dx = aXys[i - 1].position - aXys[i].position;
    var dy = aXys[i - 1].value - aXys[i].value;

    position = aXys[i - 1].position - dx * fTo;
    value = aXys[i - 1].value - dy * fTo;
  }
  return {
    position,
    value
  };
};

const getClosestExistingPoint = (
  p: Point,
  points: TimelinePoint[],
  width: number,
  height: number
): TimelinePoint | undefined => {
  const MAX_DISTANCE = 15;
  let closestPoint: TimelinePoint | undefined;
  let distance = 1000;
  for (var i = 0; i < points.length; i++) {
    const tp = points[i];
    const p2d = new Point(width * tp.position, height * (1 - tp.value));
    const d = p.distanceFrom(p2d);
    if (d < distance) {
      distance = d;
      closestPoint = tp;
    }
  }
  if (distance > MAX_DISTANCE) {
    return undefined;
  }
  return closestPoint;
};

const snapDraggedPoint = (
  draggedPoint: TimelinePoint,
  points: TimelinePoint[],
  width: number,
  height: number
) => {
  const draggedPoint2d = new Point(
    draggedPoint.position * width,
    (1 - draggedPoint.value) * height
  );
  for (const p of points) {
    if (p !== draggedPoint) {
      const p2d = new Point(p.position * width, (1 - p.value) * height);
      const distance = draggedPoint2d.distanceFrom(p2d);
      if (distance <= SNAP_DISTANCE) {
        draggedPoint.position = p.position;
        draggedPoint.value = p.value;
      }
    }
  }
};

const snapDraggedPointToFrames = (
  draggedPoint: TimelinePoint,
  snapPoints: Point[],
  width: number,
  height: number
) => {
  const draggedPoint2d = new Point(
    draggedPoint.position * width,
    (1 - draggedPoint.value) * height
  );
  for (const p of snapPoints) {
    const distance = draggedPoint2d.distanceFrom(p);
    if (distance <= SNAP_DISTANCE) {
      draggedPoint.position = p.x / width;
      draggedPoint.value = (height - p.y) / height;
      break;
    }
  }
};

interface Props {
  layer: Layer;
  effect: ItemValue;
  onChange: () => void;
}

interface State {
  draggedPoint?: TimelinePoint;
  isDragging: boolean;
}

export class EffectTimelineAdancedView extends React.Component<Props, State> {
  containerEl = React.createRef<HTMLDivElement>();
  canvasEl = React.createRef<HTMLCanvasElement>();

  draggedDotElement = React.createRef<HTMLDivElement>();
  timeTooltipEl = React.createRef<HTMLDivElement>();

  canvasWidth = 280;
  canvasHeight = 28;
  snapPoints: Point[] = [];
  lastLayersCount = 0;
  accentColor = getCSSVarValue('--accent-color');

  get timeline(): PropertyTimeline {
    const { layer, effect } = this.props;
    let tl = layer.timeline[effect.id];
    if (!tl) {
      tl = new PropertyTimeline();
      layer.timeline[effect.id] = tl;
    }
    return tl;
  }

  state: State = {
    isDragging: false
  };

  componentDidMount() {
    const canvas = this.canvasEl.current;
    if (canvas && this.containerEl.current) {
      this.canvasWidth = this.containerEl.current.getBoundingClientRect().width;
      canvas.width = this.canvasWidth;
      this.updateSnapPoints();
      this.updateCanvas();

      canvas.addEventListener('mouseleave', this.onMouseLeave);
      canvas.addEventListener('mousemove', this.onMouseMove);
      canvas.addEventListener('mousedown', this.onMouseDown);
    }
    document.addEventListener(TIMELINE_RESET, this.didResetTimeline);
  }

  didResetTimeline = () => {
    this.updateCanvas();
  };

  componentDidUpdate() {
    const newCount = getStoreState().flyer.layers.length;
    if (newCount !== this.lastLayersCount) {
      this.updateSnapPoints();
    }
  }

  componentWillUnmount() {
    const canvas = this.canvasEl.current;
    if (!canvas) {
      return;
    }

    canvas.removeEventListener('mouseleave', this.onMouseLeave);
    canvas.removeEventListener('mousemove', this.onMouseMove);
    canvas.removeEventListener('mousedown', this.onMouseDown);

    canvas.removeEventListener('mousemove', this.onDrag);
    canvas.removeEventListener('mouseup', this.onMouseUp);

    document.removeEventListener(TIMELINE_RESET, this.didResetTimeline);
  }

  updateSnapPoints() {
    const canvas = this.canvasEl.current;
    if (!canvas) {
      return;
    }

    const layers = getStoreState().flyer.layers;
    const { width, height } = canvas.getBoundingClientRect();
    const xes = [0, width];
    layers.forEach(layer => {
      if (layer.frames.length > 1 && !layer.syncPlayback) {
        const frameWidth = width / layer.frames.length;
        for (let i = 1; i < layer.frames.length; i++) {
          const x = frameWidth * i;
          if (xes.indexOf(x) === -1) {
            xes.push(x);
          }
        }
      }
    });
    this.snapPoints = [];
    xes.forEach(x => {
      this.snapPoints.push(new Point(x, 0));
      this.snapPoints.push(new Point(x, height - TOP_PADDING));
    });
    this.lastLayersCount = layers.length;
  }

  onMouseLeave = () => {
    if (!this.state.isDragging) {
      this.setState(
        {
          draggedPoint: undefined
        },
        this.updateCanvas
      );
    }
  };

  onMouseMove = (e: MouseEvent) => {
    // console.log(
    //   `MOUSE MOVE - over:${this.state.isOver} - x:${this.state.mouseX} - y:${this.state.mouseY}`
    // );
    const { isDragging } = this.state;
    // if (!isOver && !isDragging) {
    //   return;
    // }
    const canvas = this.canvasEl.current;
    if (!canvas || isDragging) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left; // x position within the element.
    const mouseY = e.clientY - rect.top; // y position within the element.

    let propertyTimeline = this.timeline;

    const closestExistingPoint = getClosestExistingPoint(
      new Point(mouseX, mouseY),
      propertyTimeline.points,
      rect.width,
      rect.height
    );
    if (closestExistingPoint) {
      this.setState(
        {
          draggedPoint: closestExistingPoint
        },
        this.updateCanvas
      );
      return;
    }

    const mousePoint: TimelinePoint = {
      position: mouseX / rect.width,
      value: (rect.height - mouseY) / rect.height
    };

    const closestPoint = getClosestPointOnLines(
      mousePoint,
      propertyTimeline.points
    );
    const closestPoint2D = new Point(
      closestPoint.position * rect.width,
      (1 - closestPoint.value) * rect.height
    );
    const distance = closestPoint2D.distanceFrom(new Point(mouseX, mouseY));

    this.setState(
      {
        draggedPoint: distance < 15 ? closestPoint : undefined
      },
      this.updateCanvas
    );
  };

  onMouseDown = (e: MouseEvent) => {
    if (!this.state.draggedPoint) {
      return;
    }
    // if draggedPoint is not included in the timeline, add it in the right place
    var arr = this.timeline.points;
    const existingPoint = arr.filter(
      p => p.position === this.state.draggedPoint!.position
    );
    if (!existingPoint.length) {
      // add point
      var newIndex = 0;
      for (var i = 0; i < arr.length; i++) {
        const p = arr[i];
        if (p.position <= this.state.draggedPoint.position) {
          newIndex = i;
        } else {
          break;
        }
      }
      arr.splice(newIndex + 1, 0, this.state.draggedPoint);
    }
    this.setState({ isDragging: true }, () => {
      this.onMouseMove(e);
      this.updateDragTooltip();
    });
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.onMouseUp);
  };

  onDrag = (e: MouseEvent) => {
    const canvas = this.canvasEl.current;
    const { draggedPoint } = this.state;
    if (!canvas || !draggedPoint) {
      return;
    }
    const propertyTimeline = this.timeline;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left; // x position within the element.
    const mouseY = e.clientY - rect.top; // y position within the element.

    const index = propertyTimeline.points.indexOf(draggedPoint);
    if (index === -1) {
      const error = new Error(
        `Can't find draggedPoint in timeline points with length ${propertyTimeline.points.length}`
      );
      console.log(draggedPoint);
      console.log(propertyTimeline.points);
      reportBFError(error);
      throw error;
    }
    if (index === 0) {
      draggedPoint.position = 0;
    } else if (index === propertyTimeline.points.length - 1) {
      draggedPoint.position = 1;
    } else {
      const prevItem = propertyTimeline.points[index - 1];
      const nextItem = propertyTimeline.points[index + 1];
      if (!prevItem) {
        const error = new Error(
          `Could not find prevItem with index ${index - 1} - points length: ${
            propertyTimeline.points.length
          }`
        );
        console.log(propertyTimeline.points);
        reportBFError(error);
        throw error;
      }
      if (!nextItem) {
        const error = new Error(
          `Could not find nextItem with index ${index + 1} - points length: ${
            propertyTimeline.points.length
          }`
        );
        console.log(propertyTimeline.points);
        reportBFError(error);
        throw error;
      }
      draggedPoint.position = clamp(
        mouseX / rect.width,
        prevItem.position,
        nextItem.position
      );
    }
    const height = rect.height - TOP_PADDING;
    draggedPoint.value = clamp((height - (mouseY - TOP_PADDING)) / height);
    // console.log('VALUE: ' + draggedPoint.value);
    snapDraggedPoint(
      draggedPoint,
      propertyTimeline.points,
      rect.width,
      rect.height
    );
    snapDraggedPointToFrames(draggedPoint, this.snapPoints, rect.width, height);
    this.updateCanvas();
    this.updateDragTooltip();
  };

  onMouseUp = () => {
    // if new dragged point is too close to an other point, remove it
    if (this.state.draggedPoint) {
      const rect = this.canvasEl.current!.getBoundingClientRect();
      const arr = this.timeline.points;
      const draggedPoint2d = new Point(
        rect.width * this.state.draggedPoint.position,
        rect.height * (1 - this.state.draggedPoint.value)
      );
      for (let p of arr) {
        if (p !== this.state.draggedPoint) {
          const p2d = new Point(
            rect.width * p.position,
            rect.height * (1 - p.value)
          );
          if (draggedPoint2d.distanceFrom(p2d) <= SNAP_DISTANCE) {
            const index = arr.indexOf(this.state.draggedPoint);
            arr.splice(index, 1);
            break;
          }
        }
      }
      this.props.onChange();
    }
    this.setState(
      {
        isDragging: false,
        draggedPoint: undefined
      },
      this.updateCanvas
    );

    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.onMouseUp);
  };

  updateCanvas = () => {
    const canvas = this.canvasEl.current;
    if (!canvas) {
      return;
    }
    const { layer } = this.props;
    const { draggedPoint } = this.state;
    const ctx = canvas.getContext('2d')!;
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight - TOP_PADDING;
    const { left, top } = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, width, height + TOP_PADDING);
    ctx.fillStyle = getCSSVarValue('--menu-bg-color');
    ctx.globalAlpha = 0.5;
    ctx.fillRect(0, 0, width, height + TOP_PADDING);
    ctx.globalAlpha = 1;

    let propertyTimeline = this.timeline;
    // draw motion
    const channel = this.props.effect.id;
    if (channel !== TIMELINE_VISIBILITY_KEY) {
      const effect = layer.effects.find(
        fx => (fx.constructor as any).id === channel
      );
      if (effect) {
        ctx.strokeStyle = getCSSVarValue('--menu-color');
        ctx.lineWidth = 1;
        let x = 0;
        const RESOLUTION = 1;
        let previousPoint: Point | undefined;
        while (x < width + RESOLUTION) {
          let position = x > 0 ? x / width : 0;
          // position *= this.motionSource!.getVideoDuration();
          let value = getSignalAt(effect.signalSource, position);
          value *= propertyTimeline.getValueAt(position);
          const point = new Point(x, (1 - value) * height);
          if (previousPoint) {
            ctx.beginPath();
            ctx.moveTo(previousPoint.x, previousPoint.y + TOP_PADDING);
            ctx.lineTo(point.x, point.y + TOP_PADDING);
            ctx.stroke();
          }
          previousPoint = point;
          x += RESOLUTION;
        }
      }
    }

    // drawn snap points
    ctx.strokeStyle = getCSSVarValue('--menu-divider-color');
    ctx.lineWidth = 1;
    for (let i = 0; i < this.snapPoints.length; i += 2) {
      const p = this.snapPoints[i];
      if (p.x > 0 && p.x < width) {
        ctx.beginPath();
        ctx.moveTo(p.x, TOP_PADDING);
        ctx.lineTo(p.x, TOP_PADDING + height);
        ctx.stroke();
      }
    }

    ctx.fillStyle = this.accentColor;
    ctx.strokeStyle = this.accentColor;
    ctx.lineWidth = 2;
    for (let i = 1; i < propertyTimeline.points.length; i++) {
      const prevPoint = propertyTimeline.points[i - 1];
      const point = propertyTimeline.points[i];
      ctx.beginPath();
      ctx.moveTo(
        width * prevPoint.position,
        height * (1 - prevPoint.value) + TOP_PADDING
      );
      ctx.lineTo(
        width * point.position,
        height * (1 - point.value) + TOP_PADDING
      );
      ctx.stroke();
    }

    const el = this.draggedDotElement.current;
    if (!el) {
      return;
    }
    if (draggedPoint) {
      el.style.display = 'block';
      el.style.left = left + width * draggedPoint.position + 'px';
      el.style.top =
        top + TOP_PADDING + height * (1 - draggedPoint.value) + 'px';
    } else {
      el.style.display = 'none';
    }
  };

  updateDragTooltip = () => {
    const { draggedPoint } = this.state;
    const tooltip = this.timeTooltipEl.current;
    if (this.containerEl.current && tooltip && draggedPoint) {
      const containerRect = this.containerEl.current.getBoundingClientRect();
      const x = containerRect.x + containerRect.width * draggedPoint.position;
      const y =
        containerRect.y + containerRect.height * (1 - draggedPoint.value);
      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y - 2}px`;
      tooltip.style.display = 'block';
      let millis =
        getStoreState().flyer.motionSource.getDuration() *
        draggedPoint.position;
      tooltip.textContent = formatTimestamp(millis);
    }
  };

  render() {
    const { isBeatflyerLite } = getStoreState().app;
    return (
      <div ref={this.containerEl} className="effect-advanced-timeline">
        <canvas
          ref={this.canvasEl}
          height={isBeatflyerLite ? 60 : 80}
          style={{
            backgroundImage: `url(${getStoreState().app.audioWaveSrc})`
          }}
        />
        <div ref={this.draggedDotElement} className="dragged-point" />
        {this.state.isDragging && (
          <div ref={this.timeTooltipEl} className="drag-time">
            00:00:00
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/EffectTimelineAdvancedView.tsx